import { Flex } from '@brightdrop/bd-ui';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  Divider,
  Skeleton,
} from '@mui/material';
import type React from 'react';
import { Fragment } from 'react';
import ChevronDownIcon from '../assets/icons/chevron-down.svg?react';
import useStyles from './RenderCardedRow.styles';

const SKELETON_ROW_KEYS = [
  'carded-skeleton-row-0',
  'carded-skeleton-row-1',
  'carded-skeleton-row-2',
];

/**
 * Skeleton component for loading state of carded rows.
 *
 * @param {boolean} renderAccordionMobileView - Whether to render accordion mobile view skeleton.
 * @param {boolean} hideCardedRowCheckbox - Whether to hide the checkbox skeleton.
 *
 * @returns {JSX.Element} The skeleton card layout.
 */
const CardedRowSkeleton: React.FC<{
  renderAccordionMobileView?: boolean;
  hideCardedRowCheckbox?: boolean;
}> = ({ renderAccordionMobileView = false, hideCardedRowCheckbox = false }) => {
  const { classes } = useStyles();

  const firstCellSkeleton = () => (
    <Flex center>
      {!hideCardedRowCheckbox && (
        <Skeleton variant="rounded" width={20} height={20} />
      )}
      <Skeleton variant="text" width={200} height={24} sx={{ ml: 1 }} />
    </Flex>
  );

  return (
    <Card
      sx={{
        marginBottom: 2,
        padding: 0,
        backgroundColor: 'background.paper',
      }}
    >
      {renderAccordionMobileView ? (
        <Accordion
          disableGutters
          defaultExpanded
          className={classes.accordionContainer}
        >
          <AccordionSummary
            expandIcon={<ChevronDownIcon />}
            className={classes.accordionSummary}
          >
            {firstCellSkeleton()}
          </AccordionSummary>
          <AccordionDetails sx={{ padding: '0px' }}>
            {SKELETON_ROW_KEYS.map((rowKey) => (
              <Fragment key={rowKey}>
                <Box className={classes.cardItemContainer}>
                  <Box className={classes.cardItemLabel}>
                    <Skeleton variant="text" width={120} height={20} />
                  </Box>
                  <Box
                    className={classes.cardItemValue}
                    minWidth="fit-content"
                    textAlign="right"
                  >
                    <Skeleton variant="text" width={80} height={20} />
                  </Box>
                </Box>
              </Fragment>
            ))}
          </AccordionDetails>
        </Accordion>
      ) : (
        <>
          <CardContent className={classes.assetListCardContent}>
            <Box display="flex" marginBottom={2}>
              {firstCellSkeleton()}
            </Box>
            {SKELETON_ROW_KEYS.map((rowKey, index) => (
              <Fragment key={rowKey}>
                {index > 0 && <Divider sx={{ marginY: '8px' }} />}
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  marginBottom="8px"
                  width="100%"
                >
                  <Box className={classes.assetCardItemLabel}>
                    <Skeleton variant="text" width={120} height={20} />
                  </Box>
                  <Box
                    className={classes.assetCardItemValue}
                    minWidth="fit-content"
                    textAlign="right"
                  >
                    <Skeleton variant="text" width={80} height={20} />
                  </Box>
                </Box>
              </Fragment>
            ))}
          </CardContent>
        </>
      )}
    </Card>
  );
};

export default CardedRowSkeleton;
